// src/components/classStudents/TuitionFeeTab.js
import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  subscribeTuitionSettings,
  subscribeTuitionFeesByClass,
  recordStudentTuitionCollected,
  cancelStudentTuition
} from '../../FirebaseService';
import './TuitionFeeTab.css';

// Helper chuẩn hoá so sánh trạng thái Nghỉ luôn (tránh lệch chữ hoa/thường)
const isStudentDropped = (status) => {
  if (!status) return false;
  return status.toString().trim().toLowerCase() === 'nghỉ luôn';
};

const TuitionFeeTab = ({ students = [], classId, displayClassName, currentYearId }) => {
  const [tuitionMap, setTuitionMap] = useState({});
  const [feeSettings, setFeeSettings] = useState({ feePerStudent: 100000 });
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // 'all' | 'unpaid' | 'collected' | 'confirmed'
  const [showDroppedStudents, setShowDroppedStudents] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  // 🌟 STATE QUẢN LÝ MODAL XÁC NHẬN THU / HỦY TIỀN
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'collect', // 'collect' | 'cancel'
    student: null,
    notes: '',
    error: ''
  });

  const catechistName = localStorage.getItem('catechistFullName') || 'Giáo lý viên';
  const catechistUid = localStorage.getItem('uid') || '';

  // 1. Lắng nghe Realtime Mức Học Phí
  useEffect(() => {
    if (!currentYearId) return;
    const unsub = subscribeTuitionSettings(currentYearId, (settings) => {
      setFeeSettings(settings);
    });
    return () => unsub();
  }, [currentYearId]);

  // 2. Lắng nghe Realtime Dữ liệu Học Phí của Lớp Hiện Tại
  useEffect(() => {
    if (!currentYearId || !classId) return;
    const unsub = subscribeTuitionFeesByClass(currentYearId, classId, (data) => {
      setTuitionMap(data || {});
    });
    return () => unsub();
  }, [currentYearId, classId]);

  const feePerStudent = feeSettings.feePerStudent || 100000;

  // 3. Đếm số em nghỉ luôn trong lớp
  const droppedCount = useMemo(() => {
    return students.filter((s) => isStudentDropped(s.status)).length;
  }, [students]);

  // 4. Thống kê nhanh
  const stats = useMemo(() => {
    const activeScopedList = students.filter((s) => {
      if (!showDroppedStudents && isStudentDropped(s.status)) return false;
      return true;
    });

    const total = activeScopedList.length;
    let collectedCount = 0;
    let confirmedCount = 0;

    activeScopedList.forEach((s) => {
      const record = tuitionMap[s.id];
      if (record && (record.status === 'collected' || record.status === 'confirmed')) {
        collectedCount++;
        if (record.status === 'confirmed') {
          confirmedCount++;
        }
      }
    });

    const pendingConfirmationCount = collectedCount - confirmedCount;
    const unpaidCount = total - collectedCount;

    return {
      total,
      collectedCount,
      confirmedCount,
      pendingConfirmationCount,
      unpaidCount,
      totalMoneyCollected: collectedCount * feePerStudent,
      moneyPendingBHC: pendingConfirmationCount * feePerStudent
    };
  }, [students, tuitionMap, feePerStudent, showDroppedStudents]);

  // 5. Lọc danh sách học sinh hiển thị
  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const dropped = isStudentDropped(s.status);

      if (!showDroppedStudents && dropped) {
        return false;
      }

      const record = tuitionMap[s.id];
      const status = record?.status || 'unpaid';

      if (filterStatus === 'unpaid' && status !== 'unpaid') return false;
      if (filterStatus === 'collected' && status !== 'collected') return false;
      if (filterStatus === 'confirmed' && status !== 'confirmed') return false;

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const fullName = (s.fullName || '').toLowerCase();
        const holyName = (s.holyName || '').toLowerCase();
        const id = (s.id || '').toLowerCase();
        return fullName.includes(term) || holyName.includes(term) || id.includes(term);
      }

      return true;
    });
  }, [students, tuitionMap, filterStatus, searchTerm, showDroppedStudents]);

  // 6. Mở Modal Thu tiền
  const handleOpenCollectModal = (student) => {
    setModalState({
      isOpen: true,
      mode: 'collect',
      student,
      notes: '',
      error: ''
    });
  };

  // 7. Mở Modal Hủy thu tiền
  const handleOpenCancelModal = (student) => {
    setModalState({
      isOpen: true,
      mode: 'cancel',
      student,
      notes: '',
      error: ''
    });
  };

  const handleCloseModal = () => {
    if (processingId) return;
    setModalState({
      isOpen: false,
      mode: 'collect',
      student: null,
      notes: '',
      error: ''
    });
  };

  // 8. Thực hiện Thu tiền từ Modal
  const handleConfirmCollect = async () => {
    const student = modalState.student;
    if (!student) return;

    try {
      setProcessingId(student.id);
      setModalState((prev) => ({ ...prev, error: '' }));

      const res = await recordStudentTuitionCollected(
        currentYearId,
        student.id,
        classId,
        catechistName,
        catechistUid,
        '',
        modalState.notes
      );

      if (!res.success) {
        setModalState((prev) => ({ ...prev, error: res.message || 'Lỗi khi ghi nhận thu tiền' }));
      } else {
        handleCloseModal();
      }
    } catch (err) {
      setModalState((prev) => ({ ...prev, error: err.message || 'Lỗi kết nối hệ thống' }));
    } finally {
      setProcessingId(null);
    }
  };

  // 9. Thực hiện Hủy thu từ Modal
  const handleConfirmCancel = async () => {
    const student = modalState.student;
    if (!student) return;

    try {
      setProcessingId(student.id);
      setModalState((prev) => ({ ...prev, error: '' }));

      const res = await cancelStudentTuition(currentYearId, student.id);
      if (!res.success) {
        setModalState((prev) => ({ ...prev, error: res.message || 'Không thể hủy thu tiền' }));
      } else {
        handleCloseModal();
      }
    } catch (err) {
      setModalState((prev) => ({ ...prev, error: err.message || 'Lỗi kết nối máy chủ' }));
    } finally {
      setProcessingId(null);
    }
  };

  // 10. Xuất Excel
  const handleExportExcel = () => {
    const headers = [
      'STT',
      'Mã ID',
      'Tên Thánh',
      'Họ và Tên',
      'Tình Trạng Học',
      'Trạng Thái Học Phí',
      'Số Tiền (VNĐ)',
      'Người Thu Tiền',
      'Ngày Thu',
      'BHC Xác Nhận',
      'Ghi Chú'
    ];

    const dataRows = filteredStudents.map((s, idx) => {
      const record = tuitionMap[s.id];
      let statusText = 'Chưa đóng';
      let amount = 0;
      let collectedBy = '';
      let collectedDate = '';
      let confirmedBy = '';

      if (record?.status === 'confirmed') {
        statusText = 'BHC đã nhận tiền';
        amount = feePerStudent;
        collectedBy = record.collectedBy || '';
        collectedDate = record.collectedDate || '';
        confirmedBy = record.confirmedBy || 'Đã xác nhận';
      } else if (record?.status === 'collected') {
        statusText = 'GLV đã thu (Chờ nộp BHC)';
        amount = feePerStudent;
        collectedBy = record.collectedBy || '';
        collectedDate = record.collectedDate || '';
        confirmedBy = 'Chưa xác nhận';
      }

      return [
        idx + 1,
        s.id,
        s.holyName || '',
        s.fullName,
        s.status || 'Còn đi học',
        statusText,
        amount,
        collectedBy,
        collectedDate,
        confirmedBy,
        record?.notes || ''
      ];
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    XLSX.utils.book_append_sheet(wb, ws, 'HocPhi');
    XLSX.writeFile(wb, `HocPhi_${displayClassName.replace(/\s+/g, '_')}.xlsx`);
  };

  const todayStr = new Date().toLocaleDateString('vi-VN');

  return (
    <div className="tuition-tab-container">
      {/* KHUNG THỐNG KÊ NHANH */}
      <div className="tuition-summary-grid">
        <div className="summary-card total">
          <span className="sc-label">Sĩ số theo dõi</span>
          <span className="sc-value">{stats.total} <small>em</small></span>
          <span className="sc-sub">Mức phí: {feePerStudent.toLocaleString('vi-VN')} đ/em</span>
        </div>

        <div className="summary-card collected">
          <span className="sc-label">Đã thu tại lớp</span>
          <span className="sc-value">{stats.collectedCount}/{stats.total}</span>
          <span className="sc-sub">{stats.totalMoneyCollected.toLocaleString('vi-VN')} đ</span>
        </div>

        <div className="summary-card pending">
          <span className="sc-label">Chờ nộp BHC</span>
          <span className="sc-value">{stats.pendingConfirmationCount} <small>em</small></span>
          <span className="sc-sub">Cần nộp: {stats.moneyPendingBHC.toLocaleString('vi-VN')} đ</span>
        </div>

        <div className="summary-card confirmed">
          <span className="sc-label">BHC đã xác nhận</span>
          <span className="sc-value">{stats.confirmedCount} <small>em</small></span>
          <span className="sc-sub">{(stats.confirmedCount * feePerStudent).toLocaleString('vi-VN')} đ</span>
        </div>
      </div>

      {/* THANH BỘ LỌC, TÌM KIẾM & CÔNG TẮC GẠT */}
      <div className="tuition-controls-bar">
        <div className="tuition-controls-left">
          <div className="search-box">
            <input
              type="text"
              placeholder="🔍 Tìm tên hoặc ID thiếu nhi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Tất cả ({filteredStudents.length})</option>
            <option value="unpaid">Chưa đóng ({stats.unpaidCount})</option>
            <option value="collected">GLV đã thu - Chờ BHC ({stats.pendingConfirmationCount})</option>
            <option value="confirmed">BHC đã nhận ({stats.confirmedCount})</option>
          </select>

          {/* CÔNG TẮC ẨN/HIỆN THIẾU NHI NGHỈ LUÔN */}
          <label className="toggle-dropped-wrap" title="Bật để xem các thiếu nhi đã nghỉ luôn">
            <div className="t-switch">
              <input
                type="checkbox"
                checked={showDroppedStudents}
                onChange={(e) => setShowDroppedStudents(e.target.checked)}
              />
              <span className="t-slider"></span>
            </div>
            <span className="t-label-text">
              {showDroppedStudents ? 'Đang hiện Nghỉ luôn' : 'Ẩn Nghỉ luôn'}
              {droppedCount > 0 && <span className="dropped-tag">{droppedCount}</span>}
            </span>
          </label>
        </div>

        <button className="export-excel-btn" onClick={handleExportExcel}>
          📥 Xuất Excel
        </button>
      </div>

      {/* BẢNG DANH SÁCH THIẾU NHI */}
      <div className="table-wrapper">
        <table className="student-table tuition-data-table">
          <thead>
            <tr>
              <th style={{ width: '45px', textAlign: 'center' }}>STT</th>
              <th style={{ width: '75px', textAlign: 'center' }}>Mã ID</th>
              <th style={{ width: '100px' }}>Tên Thánh</th>
              <th className="sticky-name-col">Họ và Tên</th>
              <th style={{ width: '110px' }}>Tình Trạng Học</th>
              <th style={{ width: '125px' }}>Trạng Thái</th>
              <th>Người Thu Tiền</th>
              <th style={{ width: '95px' }}>Ngày Thu</th>
              <th>BHC Xác Nhận</th>
              <th style={{ textAlign: 'center', width: '110px' }}>Thao Tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.length === 0 ? (
              <tr>
                <td colSpan={10} className="empty-row">Không tìm thấy thiếu nhi phù hợp</td>
              </tr>
            ) : (
              filteredStudents.map((s, idx) => {
                const record = tuitionMap[s.id];
                const isConfirmed = record?.status === 'confirmed';
                const isCollected = record?.status === 'collected';
                const isProcessing = processingId === s.id;
                const isDropped = isStudentDropped(s.status);

                return (
                  <tr
                    key={s.id}
                    className={
                      isConfirmed
                        ? 'row-confirmed'
                        : isCollected
                        ? 'row-collected'
                        : isDropped
                        ? 'row-dropped'
                        : ''
                    }
                  >
                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                    <td className="cs-id-cell" style={{ textAlign: 'center' }}>{s.id}</td>
                    <td>{s.holyName || '—'}</td>

                    <td className="sticky-name-col">
                      <strong className="student-fullname">{s.fullName}</strong>
                    </td>

                    <td>
                      {isDropped ? (
                        <span className="cs-editable-pill cs-status-badge status-off">Nghỉ luôn</span>
                      ) : (
                        <span className="cs-editable-pill cs-status-badge status-on">Còn đi học</span>
                      )}
                    </td>

                    <td>
                      {isConfirmed ? (
                        <span className="badge badge-confirmed">✅ Đã hoàn tất</span>
                      ) : isCollected ? (
                        <span className="badge badge-collected">⏳ Chờ BHC nhận</span>
                      ) : (
                        <span className="badge badge-unpaid">🔴 Chưa đóng</span>
                      )}
                    </td>

                    <td>
                      {record?.collectedBy ? (
                        <span className="collected-by-text">👤 {record.collectedBy}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    <td>
                      {record?.collectedDate ? (
                        <span className="date-tag">{record.collectedDate}</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    <td>
                      {isConfirmed ? (
                        <span className="confirmed-by-text">✔️ {record.confirmedBy || 'BHC'}</span>
                      ) : (
                        <span className="text-muted">Chưa</span>
                      )}
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      {isConfirmed ? (
                        <span className="action-locked" title="Đã bàn giao cho BHC, không thể thay đổi">
                          🔒 Đã nộp BHC
                        </span>
                      ) : isCollected ? (
                        <button
                          className="btn-action btn-cancel"
                          onClick={() => handleOpenCancelModal(s)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? '⏳' : 'Hủy thu'}
                        </button>
                      ) : (
                        <button
                          className="btn-action btn-collect"
                          onClick={() => handleOpenCollectModal(s)}
                          disabled={isProcessing}
                        >
                          {isProcessing ? '⏳' : 'Thu tiền'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 🌟 MODAL XÁC NHẬN THU TIỀN / HỦY THU (THAY THẾ ALERT & CONFIRM) */}
      {modalState.isOpen && modalState.student && (
        <div className="tft-modal-backdrop" onClick={handleCloseModal}>
          <div className="tft-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className={`tft-modal-header ${modalState.mode === 'cancel' ? 'header-cancel' : 'header-collect'}`}>
              <h3>
                {modalState.mode === 'collect' ? '💰 Xác Nhận Thu Học Phí' : '⚠️ Xác Nhận Hủy Thu Tiền'}
              </h3>
              <button
                type="button"
                className="tft-modal-close-btn"
                onClick={handleCloseModal}
                disabled={Boolean(processingId)}
              >
                ✕
              </button>
            </div>

            <div className="tft-modal-body">
              {modalState.error && (
                <div className="tft-modal-alert danger">
                  ⚠️ {modalState.error}
                </div>
              )}

              {/* KHUNG THÔNG TIN THIẾU NHI */}
              <div className="tft-modal-student-box">
                <div className="tft-sbox-line">
                  <span className="tft-sbox-label">Mã ID:</span>
                  <strong className="tft-sbox-id">{modalState.student.id}</strong>
                </div>
                <div className="tft-sbox-line">
                  <span className="tft-sbox-label">Họ và Tên:</span>
                  <strong className="tft-sbox-name">
                    {modalState.student.holyName ? `${modalState.student.holyName} ` : ''}
                    {modalState.student.fullName}
                  </strong>
                </div>
                <div className="tft-sbox-line">
                  <span className="tft-sbox-label">Lớp:</span>
                  <span>{displayClassName}</span>
                </div>
              </div>

              {/* NỘI DUNG FORM THU TIỀN */}
              {modalState.mode === 'collect' ? (
                <div className="tft-collect-details">
                  <div className="tft-amount-banner">
                    <span className="amount-label">Số tiền thu:</span>
                    <strong className="amount-value">{feePerStudent.toLocaleString('vi-VN')} đ</strong>
                  </div>

                  <div className="tft-meta-info-grid">
                    <div className="meta-info-item">
                      <span>👤 Người thu tiền:</span>
                      <strong>{catechistName}</strong>
                    </div>
                    <div className="meta-info-item">
                      <span>📅 Ngày thu:</span>
                      <strong>{todayStr}</strong>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="tft-cancel-warning">
                  <p>
                    Bạn có chắc chắn muốn <strong>HỦY</strong> khoản thu học phí của thiếu nhi{' '}
                    <strong>{modalState.student.fullName}</strong>?
                  </p>
                  <p className="warning-sub">
                    Trạng thái sẽ quay về <strong>Chưa đóng</strong> và bạn có thể thu lại bất cứ lúc nào.
                  </p>
                </div>
              )}
            </div>

            <div className="tft-modal-footer">
              <button
                type="button"
                className="tft-btn tft-btn-cancel"
                onClick={handleCloseModal}
                disabled={Boolean(processingId)}
              >
                Đóng
              </button>

              {modalState.mode === 'collect' ? (
                <button
                  type="button"
                  className="tft-btn tft-btn-confirm"
                  onClick={handleConfirmCollect}
                  disabled={Boolean(processingId)}
                >
                  {processingId ? '⏳ Đang lưu...' : '✓ Xác Nhận Đã Thu Tiền'}
                </button>
              ) : (
                <button
                  type="button"
                  className="tft-btn tft-btn-danger"
                  onClick={handleConfirmCancel}
                  disabled={Boolean(processingId)}
                >
                  {processingId ? '⏳ Đang hủy...' : '✕ Đồng Ý Hủy Thu'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuitionFeeTab;